import React, { useEffect, useRef } from 'react';

import cn from 'classnames';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CSSTransition } from 'react-transition-group';

import { Flex, Icon, UiButton, UiText } from '@shared/ui';

import cls from './WarningPopup.module.css';

interface PopupProps {
    isOpen: boolean;
    onHidePopup: () => void;
    btnActionCb?: () => void;
}

export const WarningPopup = ({ onHidePopup, btnActionCb, isOpen }: PopupProps) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen) return;

            if (e.key === 'Escape' || e.key === 'Esc') {
                e.preventDefault();
                e.stopPropagation();
                onHidePopup();
                return;
            }

            if (e.key === 'Enter') {
                btnActionCb?.();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isOpen, onHidePopup, btnActionCb]);

    return ReactDOM.createPortal(
        <CSSTransition
            in={isOpen}
            timeout={300}
            classNames={{
                enter: cls.popupEnter,
                enterActive: cls.popupEnterActive,
                exit: cls.popupExit,
                exitActive: cls.popupExitActive,
            }}
            unmountOnExit
            nodeRef={nodeRef}
        >
            <div className={cn(cls.WarningPopup)} ref={nodeRef}>
                <div className={cn(cls.WarningPopupOverlay)} onClick={onHidePopup}>
                    <div
                        className={cn(cls.WarningPopupContent)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <header className={cn(cls.WarningPopupHeader)}>
                            <Icon iconName="warning" />
                            <UiText tag="h2" variant="subHeading" weight="bold" align="center">
                                {t('EstimateStatus.return.Popup.title')}
                            </UiText>
                        </header>

                        <Flex direction="column" align="start" gap={16}>
                            <div className={cls.textCenter}>
                                <UiText variant="mediumPlain" tag="span">
                                    {t('EstimateStatus.return.Popup.Flex.UiText.first.div.base')}
                                </UiText>{' '}
                                <UiText variant="mediumPlain" tag="span" classes={cls.red}>
                                    {t('EstimateStatus.return.Popup.Flex.UiText.first.div.red')}
                                </UiText>
                            </div>
                            <UiText variant="mediumPlain" align="center">
                                {t('EstimateStatus.return.Popup.Flex.UiText.second')}
                            </UiText>
                        </Flex>

                        <footer className={cn(cls.WarningPopupFooter)}>
                            <UiButton
                                btnColor="secondary"
                                btnText={t('EstimateStatus.return.Popup.cancelButtonText')}
                                onClick={onHidePopup}
                            />
                            <UiButton
                                btnColor="danger"
                                btnText={t('EstimateStatus.return.Popup.deleteButtonText')}
                                onClick={btnActionCb}
                            />
                        </footer>
                    </div>
                </div>
            </div>
        </CSSTransition>,
        document.body
    );
};
